import { VoiceboxResponseAction } from 'bones/constants/voicebox/message';
import {
  QueryProvenanceGraphs,
  QueryProvenanceMetadata,
} from 'bones/constants/voicebox/provenance';

export interface VoiceboxPanelAnalysisData {
  analysis: string;
  data: string;
  error: string;
  loading: boolean;
}

export interface VoiceboxPanelChartData {
  customizations: string[];
  data: VoiceboxResponseAction[] | null;
  error: string;
  loading: boolean;
}

export type Attribute = {
  predicateIri: string;
} & PaginatedArray<AttributeItem>;

export type AttributeItem = {
  // mediaLiteral is set by us if binding.datatype is XSD_ANY_URI and it starts with https?://
  type: 'literal' | 'mediaLiteral';
  value: string;
};

export type Relationship = {
  direction: RelationshipDirection;
  predicateIri: string;
} & PaginatedArray<string>;

export enum RelationshipDirection {
  INCOMING = 'incoming',
  OUTGOING = 'outgoing',
}

export type PaginatedArray<T> = {
  items: T[];
  count: number;
  /** is loading initial results */
  loading: boolean;

  hasMore: boolean;
  /** is loading another page */
  loadingMore: boolean;
  loadMore: () => Promise<void>;
};

export interface VoiceboxPanelData {
  /** finished loading initial data */
  loaded: {
    body: boolean;
    label: boolean;
  };

  attributes: {
    attributes: Attribute[];
    loading: boolean;
  };
  relationships: {
    relationships: Relationship[];
    loading: boolean;
  };

  /** list of iris */
  instances: PaginatedArray<string>;

  types: {
    /** list of iris */
    types: string[];
    loading: boolean;
  };
}

export interface VoiceboxPanelHistory {
  iri?: string;
  messageId: string;
  isAnalysisOpen?: boolean;
  isChartOpen?: boolean;
}

export interface VoiceboxPanelDataContextValue {
  addPanelHistory: (panel: VoiceboxPanelHistory) => void;
  currentPanel: VoiceboxPanelHistory | null;
  panelHistory: VoiceboxPanelHistory[] | null;
  setPanelHistory: React.Dispatch<
    React.SetStateAction<VoiceboxPanelHistory[] | null>
  >;

  analysisDataByMessageId: Record<
    string,
    | {
        key: string;
        analysisDataByKey: Record<
          string,
          VoiceboxPanelAnalysisData | undefined
        >;
      }
    | undefined
  >;
  requestAnalysis: (
    messageId: string,
    analysis: string,
    retry?: boolean
  ) => void;
  setAnalysis: (messageId: string, analysis: string) => void;

  chartDataByMessageId: Record<
    string,
    | {
        key: string;
        chartDataByKey: Record<string, VoiceboxPanelChartData | undefined>;
      }
    | undefined
  >;
  requestChart: (
    messageId: string,
    customizations?: string[],
    retry?: boolean
  ) => void;
  setChartCustomizations: (messageId: string, customizations: string[]) => void;

  dataByIri: Record<string, VoiceboxPanelData | undefined>;
  getLabelOrLocalName: (iri: string) => string;
  labels: {
    /** iri to string */
    labelsByIri: Record<string, string>;
    loading: boolean;
  };
  provenance: {
    graphsByType: Record<string, QueryProvenanceGraphs>;
    metadata: QueryProvenanceMetadata;
    loading: boolean;
  };
}
